import { Button, Checkbox, DialogContent, DialogTitle, FormControlLabel, IconButton, TextField } from "@mui/material";
import styles from "./Popup.module.css";
import axios from "axios";
import { forwardRef, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { authData } from "../../components/GetAuth";
import { Close } from "@mui/icons-material";
import { Check, Image as ImageIcon, X } from "react-feather";

const CustomInput = forwardRef((props, ref) => (<TextField {...props} inputRef={ref} id="outlined-basic" label="Price(&pound;)*" variant="outlined" fullWidth />));

// Shop item (also offered on admin delivery orders). Stock is changed later from Shop > Inventory.
// cloneData: start a new item from an existing one (saved as a new item; SKU and stock start empty)
const AddDeliveryProductForm = ({ setFormOpen, setAllData, setSnackbarOpen, setSnackbarMessage, setSnackbarType, setLoading, editData, cloneData }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const { register, handleSubmit, control, setValue, watch, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: {
      title: "",
      amount: "",
      compare_at_price: "",
      sku: "",
      description: "",
      stock_qty: "",
      track_stock: true,
      low_stock_threshold: 5,
      is_shop_visible: true,
    },
  });
  const trackStock = watch("track_stock");

  useEffect(() => {
    const source = editData || cloneData;
    if (source) {
      // A clone is a new item: "(copy)" title, no SKU, its own opening stock
      setValue("title", editData ? source?.title : `${source?.title || ""} (copy)`);
      setValue("amount", source?.amount);
      setValue("compare_at_price", source?.compare_at_price ?? "");
      setValue("sku", editData ? source?.sku || "" : "");
      setValue("description", source?.description || "");
      setValue("track_stock", parseInt(source?.track_stock) === 1);
      setValue("low_stock_threshold", source?.low_stock_threshold ?? 5);
      setValue("is_shop_visible", parseInt(source?.is_shop_visible ?? 1) === 1);
      setPhotos([source?.image].concat(Array.isArray(source?.gallery) ? source.gallery : []).filter(Boolean));
    }
  }, [editData, cloneData, setValue]);

  // Photos: the first one is the main photo (Shop Now shows the second one on hover), the rest are the gallery
  const uploadImages = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    Promise.all(files.map((file) => {
      const formData = new FormData();
      formData.append("image", file);
      return axios.post(process.env.REACT_APP_API_URL + "v1/shop/upload-image", formData, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => (response.data.status === "SUCCESS" ? response.data.url : Promise.reject(new Error(response.data.message))));
    })).then((urls) => {
      setUploading(false);
      setPhotos((prev) => prev.concat(urls));
    }).catch((error) => {
      setUploading(false);
      setSnackbarOpen(true);
      setSnackbarMessage(error?.message || "A photo could not be uploaded. Please try again.");
      setSnackbarType("error");
    });
  };

  const removePhoto = (url) => setPhotos((prev) => prev.filter((p) => p !== url));
  const makeMain = (url) => setPhotos((prev) => [url].concat(prev.filter((p) => p !== url)));

  const onSubmit = (formData) => {
    const data = {
      ...formData,
      image: photos[0] || null,
      gallery: photos.slice(1),
      compare_at_price: formData.compare_at_price === "" ? null : formData.compare_at_price,
      track_stock: formData.track_stock ? 1 : 0,
      is_shop_visible: formData.is_shop_visible ? 1 : 0,
      low_stock_threshold: parseInt(formData.low_stock_threshold) || 0,
    };
    if (editData) {
      // Stock levels change through Shop > Inventory, which keeps the history
      delete data.stock_qty;
    } else {
      data.stock_qty = parseInt(formData.stock_qty) || 0;
    }

    setLoading(true);
    setFormOpen(false);
    if (editData) {
      axios.post(process.env.REACT_APP_API_URL + "v1/delivery-products/" + editData?.id, data, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");
          setAllData((prev) => {
            let temp = prev.map((i) => {
              if (i.id === editData?.id) {
                return response.data.data;
              }
              return i;
            });
            return temp;
          });
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
      }).catch((error) => {
        setLoading(false);
        if (error && error.response && error.response.data && error.response.data.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
    } else {
      axios.post(process.env.REACT_APP_API_URL + "v1/delivery-products", data, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");
          setAllData((prev) => {
            return [response.data.data].concat(prev);
          });
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
      }).catch((error) => {
        setLoading(false);
        if (error && error.response && error.response.data && error.response.data.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
    }
  };

  return (<>
    <DialogTitle id="alert-dialog-title" className={`${styles.PopupTitleBar}`}>
      {editData ? "Update Shop Item" : cloneData ? "Clone Shop Item" : "Add Shop Item"}
      <IconButton onClick={(e) => setFormOpen(false)} className={`${styles.PopupCloseBU}`} aria-label="Close"><Close /></IconButton>
    </DialogTitle>
    <DialogContent className={`${styles.PopupContent}`}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={`${styles.FormMainRow}`}>
          <div className={`${styles.FormGroupHalf}`}>
            <Controller
              control={control}
              {...register("title", { required: "This field is Required" })}
              render={({ field }) => (<TextField {...field} label="Title*" variant="outlined" fullWidth />)}
            />
            {errors.title && (<span className={`${styles.ErrorM}`}>{errors?.title?.message}</span>)}
          </div>
          <div className={`${styles.FormGroupHalf}`}>
            <Controller
              control={control}
              {...register("amount", { required: "This field is Required" })}
              render={({ field }) => (<NumericFormat {...field} customInput={CustomInput} decimalScale={2} />)}
            />

            {errors.amount && (<span className={`${styles.ErrorM}`}>{errors?.amount?.message}</span>)}
          </div>
          <div className={`${styles.FormGroupHalf}`}>
            <Controller
              control={control}
              {...register("compare_at_price")}
              render={({ field }) => (<NumericFormat {...field} customInput={TextField} label="Compare-at price (£)" variant="outlined" fullWidth decimalScale={2} allowNegative={false} />)}
            />
            <span className={`${styles.HintText}`}>The old price. Shows a Sale badge when higher than the price.</span>
          </div>
          <div className={`${styles.FormGroupHalf}`}>
            <Controller
              control={control}
              {...register("sku")}
              render={({ field }) => (<TextField {...field} label="SKU" variant="outlined" fullWidth />)}
            />
          </div>
          <div className={`${styles.FormGroupFull}`}>
            <Controller
              control={control}
              {...register("description")}
              render={({ field }) => (<TextField {...field} label="Description" variant="outlined" multiline rows={3} fullWidth />)}
            />
          </div>

          <div className={`${styles.FormGroupFull}`}>
            <div className={`${styles.PhotoGrid}`}>
              {photos.map((url, i) => (<div className={`${styles.PhotoTile}`} key={url}>
                <img src={url} alt="" />
                {i === 0 ? <span className={`${styles.PhotoMain}`}>Main</span> : <button type="button" className={`${styles.PhotoAction}`} onClick={() => makeMain(url)}>Make main</button>}
                <IconButton size="small" className={`${styles.PhotoRemove}`} onClick={() => removePhoto(url)} aria-label="Remove photo"><Close fontSize="small" /></IconButton>
              </div>))}
              <Button component="label" className={`${styles.PhotoAdd}`} disabled={uploading}>
                {uploading ? "Uploading..." : <><ImageIcon size={20} /><span>Add photos</span></>}
                <input type="file" accept="image/*" multiple hidden onChange={uploadImages} />
              </Button>
            </div>
            <span className={`${styles.HintText}`}>Shown to customers in Shop Now. The first photo is the main one.</span>
          </div>

          <div className={`${styles.FormGroupCheckBox}`}>
            <Controller
              control={control}
              name="track_stock"
              render={({ field }) => (<FormControlLabel className={`${styles.CheckBoxLabel}`} control={<Checkbox className={`${styles.PopupCheckbox}`} checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Track stock" />)}
            />
          </div>
          <div className={`${styles.FormGroupCheckBox}`}>
            <Controller
              control={control}
              name="is_shop_visible"
              render={({ field }) => (<FormControlLabel className={`${styles.CheckBoxLabel}`} control={<Checkbox className={`${styles.PopupCheckbox}`} checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Show in Shop Now" />)}
            />
          </div>

          {trackStock && !editData && <div className={`${styles.FormGroupHalf}`}>
            <Controller
              control={control}
              {...register("stock_qty", { min: { value: 0, message: "Stock cannot be negative" } })}
              render={({ field }) => (<TextField {...field} type="number" label="Opening stock" variant="outlined" fullWidth inputProps={{ min: 0 }} />)}
            />
            {errors.stock_qty && (<span className={`${styles.ErrorM}`}>{errors?.stock_qty?.message}</span>)}
          </div>}
          {trackStock && <div className={`${styles.FormGroupHalf}`}>
            <Controller
              control={control}
              {...register("low_stock_threshold")}
              render={({ field }) => (<TextField {...field} type="number" label="Low stock alert at" variant="outlined" fullWidth inputProps={{ min: 0 }} />)}
            />
          </div>}
          {trackStock && editData && <div className={`${styles.FormGroupHalf}`}>
            <span className={`${styles.HintText}`}>In stock: {parseInt(editData?.stock_qty) || 0}. Change it from Shop &gt; Inventory.</span>
          </div>}
        </div>
        <div className={`${styles.ButtonAction}`}>
          <Button className={`${styles.CancelPopupBU}`} startIcon={<X size={16} />} onClick={(e) => setFormOpen(false)}>Cancel</Button>
          <Button className={`${styles.SubmitPopupBU}`} startIcon={<Check size={16} />} type="submit" disabled={uploading}>{editData ? "Update" : "Add"}</Button>
        </div>
      </form>
    </DialogContent>
  </>);
};

export default AddDeliveryProductForm;
